import uuidv4 from 'uuid/v4'
import truncate from 'lodash/truncate'
import parse from 'date-fns__legacy__/parse'
import getTime from 'date-fns__legacy__/get_time'
import messageStatus from 'Enums/messageStatus'
import quotationPaymentStatus from 'Enums/quotationPaymentStatus'
import chatListStatus from 'Enums/chatListStatus'
import historyOrigin from 'Enums/historyOrigin'
import chatMessageDate from 'Services/chatMessageDate'
import { companyAvatar } from 'Services/avatarGenerator'
import { fileAvatar } from 'Services/avatarGenerator'
import { ApiChatFile, ChatHistoryFile, ChatHistoryMessage, ApiQuoteRequestInterestedCompany, ChatCompany, ApiChatHistoryStatus, ApiChatHistoryMessage, ChatMessage, ChatFile } from 'Types'
import { truncateFilename, getFileExtension } from 'Services/fileService'
import { hostname } from 'Services/accessUrl'
/**
 * Return the chat status based on the quotation payment
 * @param {number} Current payment status of the quotation
 */
const getChatStatus = (paymentStatus: number): number => {
  const { waitingPayment, paid } = quotationPaymentStatus
  const { waiting, available, unavailable } = chatListStatus

  return {
    [waitingPayment]: waiting,
    [paid]: available,
  }[paymentStatus] || unavailable
}

/**
 * Return the chat status based on some quotation conditions
 * @param paymentStatus Current payment status of the quotation
 * @param isLostOpportunity Indicates if the supplier didn't buy the quotation in time
 */
const parseChatStatus = (paymentStatus: number, isLostOpportunity: boolean): number => {
  return isLostOpportunity || paymentStatus !== quotationPaymentStatus.paid
    ? chatListStatus.unavailable
    : getChatStatus(paymentStatus)
}

const isFileImage = (path: string): boolean => {
  const imageList = ['jpeg', 'jpg', 'gif', 'png', 'svg', 'bmp']

  return imageList.includes(path)
}

export const parsePreviewFile = (file: ChatFile): ChatHistoryFile => {
  const fileExtension = getFileExtension(file.name)
  const isImage = isFileImage(fileExtension)

  return {
    isImage: isImage,
    title: truncateFilename(file.name),
    url: file.url,
    thumbnail: isImage ? file.url : fileAvatar(fileExtension),
    width: null,
    height: null,
  }
}

export const parseApiFile = (data: ApiChatFile): ChatHistoryFile => {
  const fileExtension = getFileExtension(data.fileName)

  return {
    isImage: data.isImage,
    title: truncateFilename(data.fileName),
    url: data.isImage ? data.imageLargeUrl : data.fileUrl,
    thumbnail: data.isImage ? data.imageSmallUrl : fileAvatar(fileExtension),
    width: data.widthImageLarge,
    height: data.heightImageLarge
  }
}

export const parseChatCompany = (data: ApiQuoteRequestInterestedCompany): ChatCompany => {
  return {
    alreadyEvaluated: data.alreadyEvaluated,
    id: data.id,
    logo: companyAvatar(data.companyLogoUrl, data.fancyName),
    name: data.fancyName,
    status: data.paymentSituationFormat,
    statusId: data.paymentSituation,
    lastMessage: null,
    lastUpdated: null,
    unreadMessages: 0,
    url: `/fornecedor/${data.slug}`,
    phone: `(${data.phoneDDD}) ${data.phoneNumber}`,
    order: data.order,
    discardReason: data.discardReason
  }
}

export const parseChatMessage = (data: ApiChatHistoryMessage): ChatHistoryMessage => {
  const messageDate = parse(data.dateCreated)
  const { read, delivered } = messageStatus

  return {
    id: data.id,
    identifier: data.identifier || data.id.toString(),
    message: data.description,
    status: data.alreadyRead ? read : delivered,
    date: chatMessageDate(messageDate),
    datetime: messageDate,
    user: data.userName,
    company: data.companyName,
    budget: data.budget || 0,
    files: data.files.map(parseApiFile),
    origin: data.historyOrigin,
  }
}

export const parseChatCompanyStatus = (company: ChatCompany, status: ApiChatHistoryStatus) => {
  const lastUpdated = parse(status.lastMessageDate)

  return Object.assign({}, company, {
    lastMessage: truncate(status.lastMessage, { length: 20 }),
    lastUpdated: chatMessageDate(lastUpdated),
    unreadMessages: status.unreadMessages,
  })
}

export const parseTemporaryMessage = (
  message: ChatMessage,
  userName: string,
  companyName: string = '',
  origin: number = historyOrigin.customer
): ChatHistoryMessage => {
  const currentTime = parse(Date.now())
  const timestamp = Math.floor(getTime(currentTime) / 1000)

  return {
    id: timestamp,
    identifier: uuidv4(),
    message: message.message,
    date: chatMessageDate(currentTime),
    datetime: currentTime,
    files: message.files.map(parsePreviewFile),
    user: userName,
    company: companyName,
    origin: origin,
    budget: message.budget,
    status: messageStatus.sending,
  }
}

export const parseSystemMessage = (message: string): ChatHistoryMessage => {
  const currentTime = parse(Date.now())
  const timestamp = Math.floor(getTime(currentTime) / 1000)

  return {
    id: timestamp,
    identifier: uuidv4(),
    message: message,
    status: messageStatus.none,
    date: chatMessageDate(currentTime),
    datetime: currentTime,
    user: null,
    company: null,
    files: [],
    origin: historyOrigin.system,
  }
}

export default {
  parsePreviewFile,
  parseApiFile,
  parseChatCompanyStatus,
  parseChatCompany,
  parseChatMessage,
  parseTemporaryMessage,
  parseSystemMessage,
}